import React, { useState, useRef, useEffect } from 'react';
import { useChat } from '../context/ChatContext';
import { useCompany } from '../context/CompanyContext';
import { Spinner } from '../components/UI';
import { fmt } from '../utils/formatters';

const QUICK_REPLIES = [
    'What are your services?',
    'I want to book an appointment',
    'What events do you have?',
    'I want to place an order',
    'Do you have available flights?',
];

function MessageBubble({ msg }) {
    const isUser = msg.role === 'user';
    const timeStr = `${msg.ts.getHours().toString().padStart(2, '0')}:${msg.ts.getMinutes().toString().padStart(2, '0')}`;
    return (
        <div style={{
            display: 'flex', flexDirection: isUser ? 'row-reverse' : 'row',
            alignItems: 'flex-end', gap: 8, marginBottom: 8,
        }}>
            {!isUser && (
                <div style={{
                    width: 36, height: 36, borderRadius: '50%', backgroundColor: 'var(--bg-surface)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16, flexShrink: 0
                }}>
                    🤖
                </div>
            )}
            <div style={{
                maxWidth: '78%', padding: '10px 14px',
                borderRadius: isUser ? '18px 18px 4px 18px' : '18px 18px 18px 4px',
                backgroundColor: isUser ? 'var(--brand-600)' : 'var(--bg-card)',
                border: isUser ? 'none' : '1px solid var(--border)',
            }}>
                <p style={{
                    color: isUser ? '#fff' : 'var(--text-primary)', fontSize: 15, lineHeight: 1.5,
                    wordBreak: 'break-word'
                }}>{msg.content}</p>
                <p style={{
                    color: isUser ? 'rgba(255,255,255,0.6)' : 'var(--text-muted)',
                    fontSize: 11, marginTop: 4, textAlign: 'right'
                }}>{fmt.time(timeStr)}</p>
            </div>
        </div>
    );
}

function TypingIndicator() {
    return (
        <div style={{ display: 'flex', alignItems: 'flex-end', gap: 8, marginBottom: 8 }}>
            <div style={{
                width: 36, height: 36, borderRadius: '50%', backgroundColor: 'var(--bg-surface)',
                display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16
            }}>🤖</div>
            <div style={{
                padding: '14px 18px', backgroundColor: 'var(--bg-card)', borderRadius: '18px 18px 18px 4px',
                border: '1px solid var(--border)', display: 'flex', gap: 6, alignItems: 'center'
            }}>
                {[0, 1, 2].map(i => (
                    <div key={i} style={{
                        width: 8, height: 8, borderRadius: '50%', backgroundColor: 'var(--brand-300)',
                        animation: `bounce 1.2s ${i * 0.2}s infinite ease-in-out`,
                    }} />
                ))}
            </div>
        </div>
    );
}

export default function ChatScreen() {
    const { messages, loading, sendMessage, clearChat } = useChat();
    const { company } = useCompany();
    const [text, setText] = useState('');
    const bottomRef = useRef(null);
    const inputRef = useRef(null);

    useEffect(() => {
        bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [messages.length, loading]);

    const handleSend = async () => {
        const t = text.trim();
        if (!t || loading) return;
        setText('');
        await sendMessage(t);
        inputRef.current?.focus();
    };

    const handleKey = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); }
    };

    return (
        <div style={{ display: 'flex', flexDirection: 'column', height: '100vh', maxHeight: '100vh' }}>
            {/* Header */}
            <div style={{
                padding: '16px 20px', backgroundColor: 'var(--bg-card)',
                borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: 12, flexShrink: 0
            }}>
                <div style={{
                    width: 40, height: 40, borderRadius: '50%', backgroundColor: 'var(--bg-surface)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20
                }}>🤖</div>
                <div>
                    <p style={{ fontFamily: 'var(--font-display)', fontSize: 16, fontWeight: 700 }}>
                        {company?.persona?.name || 'GAAF'}
                    </p>
                    <p style={{ color: 'var(--brand-300)', fontSize: 11 }}>AI Agent · Online</p>
                </div>
                <button onClick={clearChat} style={{
                    marginLeft: 'auto', background: 'none', border: 'none',
                    color: 'var(--text-muted)', cursor: 'pointer', fontSize: 18, padding: 4
                }} title="Clear chat">🗑</button>
            </div>

            {/* Messages */}
            <div style={{ flex: 1, overflowY: 'auto', padding: '16px 16px 8px' }}>
                {messages.map(msg => <MessageBubble key={msg.id} msg={msg} />)}
                {loading && <TypingIndicator />}
                <div ref={bottomRef} />
            </div>

            {/* Quick replies */}
            {messages.length <= 2 && !loading && (
                <div style={{ borderTop: '1px solid var(--border)', padding: '8px 0', flexShrink: 0, overflowX: 'auto' }}>
                    <div style={{ display: 'flex', gap: 8, padding: '0 16px', width: 'max-content' }}>
                        {QUICK_REPLIES.map(q => (
                            <button key={q} onClick={() => sendMessage(q)} style={{
                                backgroundColor: 'var(--bg-card)', border: '1px solid var(--border)',
                                borderRadius: 'var(--radius-full)', padding: '6px 14px',
                                color: 'var(--brand-200)', fontSize: 13, cursor: 'pointer', whiteSpace: 'nowrap',
                            }}>{q}</button>
                        ))}
                    </div>
                </div>
            )}

            {/* Input bar */}
            <div style={{
                display: 'flex', alignItems: 'flex-end', gap: 8, padding: 12,
                borderTop: '1px solid var(--border)', backgroundColor: 'var(--bg-card)', flexShrink: 0
            }}>
                <textarea
                    ref={inputRef}
                    value={text}
                    onChange={e => setText(e.target.value)}
                    onKeyDown={handleKey}
                    placeholder="Type a message…"
                    rows={1}
                    style={{
                        flex: 1, backgroundColor: 'var(--bg-primary)', border: '1px solid var(--border)',
                        borderRadius: 20, padding: '10px 16px', color: 'var(--text-primary)',
                        fontSize: 15, resize: 'none', maxHeight: 120, overflowY: 'auto',
                        fontFamily: 'var(--font-body)', lineHeight: 1.4,
                    }}
                />
                <button onClick={handleSend} disabled={!text.trim() || loading} style={{
                    width: 40, height: 40, borderRadius: '50%', border: 'none',
                    backgroundColor: text.trim() && !loading ? 'var(--brand-500)' : 'var(--bg-surface)',
                    color: '#fff', fontSize: 18, cursor: text.trim() && !loading ? 'pointer' : 'not-allowed',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                    transition: 'background 0.15s',
                }}>
                    {loading ? <Spinner size={18} color="#fff" /> : '⬆'}
                </button>
            </div>

            <style>{`
        @keyframes bounce {
          0%, 80%, 100% { transform: scale(0); opacity: 0.4; }
          40% { transform: scale(1); opacity: 1; }
        }
      `}</style>
        </div>
    );
}
